import {
  REDIRECT_MODULE_CONFIG,
  RedirectModule,
  VALID_MODULES,
  REDIRECT_LOCATION_KEY,
  REDIRECT_EXPIRATION_MS,
} from "./Redirect.config";

/**
 * Parsed URL parameters from the redirect URL
 */
export interface RedirectParams {
  module: RedirectModule | null;
  [key: string]: string | null;
}

/**
 * Stored redirect location with timestamp
 */
export interface StoredRedirectLocation {
  module: RedirectModule;
  params: Record<string, string>;
  timestamp: number;
}

/**
 * Validation result for redirect parameters
 */
export interface ValidationResult {
  isValid: boolean;
  errors: string[];
  module?: RedirectModule;
  params?: Record<string, string>;
}

/**
 * Parses URL search parameters into a structured object
 */
export const parseRedirectParams = (
  searchParams: URLSearchParams
): RedirectParams => {
  const params: RedirectParams = { module: null };

  // Get all parameters from URL
  searchParams.forEach((value, key) => {
    if (key === "module") {
      params.module = value as RedirectModule;
    } else {
      params[key] = value;
    }
  });

  return params;
};

/**
 * Validates that all required parameters are present for the given module
 */
export const validateRedirectParams = (
  params: RedirectParams
): ValidationResult => {
  const errors: string[] = [];

  // Check if module is provided
  if (!params.module) {
    errors.push("Missing required parameter: module");
    return { isValid: false, errors };
  }

  // Check if module is valid
  if (!VALID_MODULES.includes(params.module)) {
    errors.push(
      `Invalid module: ${params.module}. Valid modules are: ${VALID_MODULES.join(", ")}`
    );
    return { isValid: false, errors };
  }

  const moduleConfig = REDIRECT_MODULE_CONFIG[params.module];

  // Check for required parameters
  const missingParams: string[] = [];
  moduleConfig.requiredParams.forEach((requiredParam) => {
    if (!params[requiredParam] || params[requiredParam] === "") {
      missingParams.push(requiredParam);
    }
  });

  if (missingParams.length > 0) {
    errors.push(
      `Missing required parameters for ${moduleConfig.displayName}: ${missingParams.join(", ")}`
    );
  }

  if (errors.length > 0) {
    return { isValid: false, errors };
  }

  // Build validated params object
  const validatedParams: Record<string, string> = {};
  [
    ...moduleConfig.requiredParams,
    ...(moduleConfig.optionalParams || []),
  ].forEach((paramName) => {
    if (params[paramName]) {
      validatedParams[paramName] = params[paramName] as string;
    }
  });

  return {
    isValid: true,
    errors: [],
    module: params.module,
    params: validatedParams,
  };
};

/**
 * Formats validation errors into a user-friendly message
 */
export const formatValidationError = (errors: string[]): string => {
  // Always return a simple, user-friendly message
  // Don't expose technical details to end users
  return "The link you followed is invalid or has expired. Please try again or contact support if the issue persists.";
};

/**
 * Saves redirect location to localStorage with timestamp
 */
export const saveRedirectLocation = (
  module: RedirectModule,
  params: Record<string, string>
): void => {
  const redirectData: StoredRedirectLocation = {
    module,
    params,
    timestamp: Date.now(),
  };
  localStorage.setItem(REDIRECT_LOCATION_KEY, JSON.stringify(redirectData));
};

/**
 * Retrieves redirect location from localStorage if it exists and hasn't expired
 * Returns null if no redirect exists or if it has expired
 */
export const getRedirectLocation = (): StoredRedirectLocation | null => {
  try {
    const stored = localStorage.getItem(REDIRECT_LOCATION_KEY);
    if (!stored) return null;

    const redirectData: StoredRedirectLocation = JSON.parse(stored);
    const now = Date.now();
    const isExpired = now - redirectData.timestamp > REDIRECT_EXPIRATION_MS;

    if (isExpired) {
      // Clear expired redirect
      clearRedirectLocation();
      return null;
    }

    return redirectData;
  } catch (error) {
    console.error("Error retrieving redirect location:", error);
    clearRedirectLocation();
    return null;
  }
};

/**
 * Clears redirect location from localStorage
 */
export const clearRedirectLocation = (): void => {
  localStorage.removeItem(REDIRECT_LOCATION_KEY);
};
